import Vue from 'vue'
import { Route } from 'vue-router'

/**
 * Interface for components using this mixin
 */
interface HashNavigationMixin extends Vue {
  _hashNavigationHandled: boolean
  $route: Route
  // Methods provided by the mixin
  handleHashNavigation(retryCount?: number): void
  scrollToSection(sectionId: string, collapsibleKey?: string): Promise<void>
  // Optional methods to be implemented by components
  getCollapsibleKeyForSection?(sectionId: string): string | undefined
  expandSection?(collapsibleKey: string): Promise<void>
  getScrollHeaderOffset?(): number
}

/**
 * Mixin for handling hash navigation to page sections
 *
 * Usage:
 * 1. Import and add to component mixins
 * 2. Implement getCollapsibleKeyForSection(sectionId) method if sections can be collapsed
 * 3. Implement expandSection(collapsibleKey) method if sections need to be expanded before scrolling
 *
 * Example:
 * ```typescript
 * import hashNavigationMixin from '@/mixins/hash-navigation-mixin'
 *
 * export default Vue.extend({
 *   mixins: [hashNavigationMixin],
 *   methods: {
 *     getCollapsibleKeyForSection(sectionId: string): string | undefined {
 *       const navItem = this.navigationItems.find(item => item.id === sectionId)
 *       return navItem?.collapsibleKey
 *     },
 *     async expandSection(collapsibleKey: string): Promise<void> {
 *       if (this.collapsibleSections[collapsibleKey] && !this.collapsibleSections[collapsibleKey].expanded) {
 *         await this.toggleSection(collapsibleKey)
 *       }
 *     },
 *     getScrollHeaderOffset(): number {
 *       return 100
 *     }
 *   }
 * })
 * ```
 *
 * Optional methods to implement in component:
 *
 * - getCollapsibleKeyForSection(sectionId: string): string | undefined
 *   Return the collapsible key for a section that needs expanding
 *
 * - async expandSection(collapsibleKey: string): Promise<void>
 *   Expand a collapsible section before scrolling
 *
 * - getScrollHeaderOffset(): number
 *   Customize the header offset (default: 80px)
 */
export default Vue.extend({
  data() {
    return {
      _hashNavigationHandled: false
    }
  },

  mounted(this: HashNavigationMixin) {
    // Only auto-handle if component hasn't manually handled it
    if (!this._hashNavigationHandled) {
      this.handleHashNavigation()
    }
  },

  watch: {
    '$route.hash': function(this: HashNavigationMixin, newHash: string) {
      if (newHash) {
        this._hashNavigationHandled = false
        this.handleHashNavigation()
      }
    }
  },

  methods: {
    /**
     * Handle hash navigation when component is mounted or route hash changes
     * Uses a retry mechanism to wait for DOM elements to be rendered
     */
    handleHashNavigation(this: HashNavigationMixin, retryCount: number = 0): void {
      if (!this.$route.hash) {
        return
      }

      // Mark as handled to prevent duplicate calls
      this._hashNavigationHandled = true

      const sectionId = this.$route.hash.substring(1) // Remove the '#' prefix
      const maxRetries = 20
      const retryDelay = 250

      // Check if the element exists yet and has content
      const element = document.getElementById(sectionId)
      const hasContent = element && (element.offsetHeight > 0 || element.children.length > 0)

      if ((!element || !hasContent) && retryCount < maxRetries) {
        // Element not ready yet, retry after a short delay
        setTimeout(() => {
          this._hashNavigationHandled = false
          this.handleHashNavigation(retryCount + 1)
        }, retryDelay)
        return
      }

      if (!element) {
        return
      }

      // Allow component to determine if section is collapsible
      const collapsibleKey = this.getCollapsibleKeyForSection?.(sectionId)

      this.$nextTick(() => {
        this.scrollToSection(sectionId, collapsibleKey)
      })
    },

    /**
     * Scroll to a specific section, optionally expanding it first if collapsible
     * @param sectionId - The DOM element ID to scroll to
     * @param collapsibleKey - Optional key for collapsible sections
     */
    async scrollToSection(
      this: HashNavigationMixin,
      sectionId: string,
      collapsibleKey?: string
    ): Promise<void> {
      // If this section has a collapsible key and component implements expandSection, expand it first
      if (collapsibleKey && this.expandSection) {
        await this.expandSection(collapsibleKey)
      }

      // Wait for DOM update after expansion
      this.$nextTick(() => {
        const element = document.getElementById(sectionId)
        if (element) {
          const headerOffset = this.getScrollHeaderOffset?.() ?? 80
          const elementPosition = element.getBoundingClientRect().top
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          })
        }
      })
    }
  }
})
